import { createContext } from "reka-ui";
import {
  computed,
  defineComponent,
  onUnmounted,
  type PropType,
  type Ref,
  shallowRef,
  type Slot,
  type SlotsType,
  toRef,
} from "vue";

export type DrawerVariant = "default" | "very-wide";

type HeldEntry = {
  slot: Ref<Slot | undefined>;
  variant: Ref<DrawerVariant>;
};

const [inject, provide] = createContext<{
  held: Ref<HeldEntry[]>;
  isOpen: Ref<boolean>;
  topVariant: Ref<DrawerVariant>;
}>("DrawerPortal");

export const Root = defineComponent({
  inheritAttrs: false,
  slots: Object as SlotsType<{
    default(props: { open: boolean; variant: DrawerVariant }): unknown;
  }>,
  setup(_props, { slots }) {
    const held = shallowRef<HeldEntry[]>([]);

    const isOpen = computed(() => held.value.length > 0);
    const topVariant = computed((): DrawerVariant => {
      const top = held.value.at(-1);
      if (top !== undefined) {
        return top.variant.value;
      }

      return "default";
    });

    provide({ held, isOpen, topVariant });

    return () => {
      return slots.default({
        open: isOpen.value,
        variant: topVariant.value,
      });
    };
  },
});

export const useIsPortalOpen = (): Ref<boolean> => {
  const context = inject(null);
  if (!context) {
    return toRef(() => false);
  }

  return context.isOpen;
};

export const useDrawerVariant = (): Ref<DrawerVariant> => {
  const context = inject(null);
  if (!context) {
    return toRef((): DrawerVariant => "default");
  }

  return context.topVariant;
};

export const Outlet = defineComponent({
  inheritAttrs: false,
  setup(_props) {
    const { held } = inject();
    const top = computed(() => held.value.at(-1));

    return () => {
      const entry = top.value;
      if (entry !== undefined) {
        const slot = entry.slot.value;
        return slot?.();
      }
    };
  },
});

/** @knipignore This is used by Drawer.Root, not detected for some reason */
export const Content = defineComponent({
  inheritAttrs: false,
  props: {
    variant: {
      type: String as PropType<DrawerVariant>,
      default: "default" satisfies DrawerVariant,
    },
  },
  setup(props, { slots }) {
    const { held } = inject();

    // Initialize with slots.default immediately to prevent a race condition
    // where the Outlet sees the entry before its slot is set
    const slot = shallowRef<Slot | undefined>(slots.default);
    const entry: HeldEntry = { slot, variant: toRef(props, "variant") };

    held.value = [...held.value, entry];
    onUnmounted(() => {
      held.value = held.value.filter((e) => e !== entry);
    });

    return () => {
      slot.value = slots.default;
      return undefined;
    };
  },
});
